/* BP-023 · Portfolio Intelligence Page — /v3/portfolio
   KPI banner + assets table + right-drawer with 4 tabs
   (Overview / Financials / Debt / Alerts) + AI Portfolio Advisor
   surfaced inline. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Building2, DollarSign, TrendingUp, ShieldAlert, Percent,
  Activity, RefreshCw, Plus, X, ChevronRight, Search, AlertTriangle,
  Sparkles, RotateCw, ThumbsUp, ThumbsDown, FileText,
} from "lucide-react";


const TIER_TONE = {
  thriving: { text: "#065f46", bg: "#a7f3d0", border: "#059669" },
  healthy:  { text: "#047857", bg: "#d1fae5", border: "#10b981" },
  watch:    { text: "#b45309", bg: "#fef3c7", border: "#f59e0b" },
  stressed: { text: "#b91c1c", bg: "#fee2e2", border: "#dc2626" },
};

const SEV_TONE = {
  high:   { text: "#b91c1c", bg: "#fee2e2" },
  medium: { text: "#b45309", bg: "#fef3c7" },
  low:    { text: "#0369a1", bg: "#e0f2fe" },
};

function fmt$(n, digits = 0) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(v)) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: digits })}`;
}
function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(v)) return "—";
  return `${v.toFixed(1)}%`;
}


export default function PortfolioIntelligencePage() {
  const [dash, setDash] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get("/v3/portfolio/dashboard"),
        api.get("/v3/portfolio/assets", { params: q ? { q } : {} }),
      ]);
      setDash(d.data);
      setAssets(a.data || []);
    } catch { toast.error("Failed to load portfolio"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const recalc = async () => {
    setRecalculating(true);
    try {
      await api.post("/v3/portfolio/recalculate");
      toast.success("Portfolio recalculated");
      await load();
    } catch { toast.error("Recalculate failed"); }
    finally { setRecalculating(false); }
  };

  const m = dash?.metrics || {};
  const tt = TIER_TONE[dash?.tier] || TIER_TONE.watch;

  return (
    <div className="min-h-screen bg-slate-50 pb-24"
          data-testid="portfolio-intelligence-page">
      <div className="max-w-[1400px] mx-auto px-4 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-teal-600">
              Portfolio Intelligence · BP-023
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">
              Portfolio Intelligence
            </h1>
            <div className="text-sm text-slate-500">
              Deterministic aggregation · 6-factor health score · alerts
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={recalc} disabled={recalculating}
                      data-testid="portfolio-recalc-btn"
                      className="text-sm px-3 py-2 rounded-md
                                  bg-gradient-to-r from-teal-500 to-emerald-500
                                  text-white hover:from-teal-600 hover:to-emerald-600
                                  flex items-center gap-1.5 disabled:opacity-50">
              {recalculating
                ? <Loader2 size={14} className="animate-spin"/>
                : <RefreshCw size={14}/>}
              Recalculate
            </button>
            <button onClick={() => setShowCreate(true)}
                      data-testid="portfolio-new-asset-btn"
                      className="text-sm px-3 py-2 rounded-md
                                  bg-slate-900 text-white hover:bg-slate-800
                                  flex items-center gap-1.5">
              <Plus size={14}/> Add Asset
            </button>
          </div>
        </div>

        {/* Hero — Health Score */}
        {dash && (
          <div className="rounded-xl border p-4 mb-4"
                data-testid="portfolio-hero"
                style={{ background: tt.bg + "44", borderColor: tt.border }}>
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  Portfolio Health
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="text-5xl font-bold tabular-nums"
                        style={{ color: tt.text }}
                        data-testid="portfolio-health-score">
                    {dash.score?.toFixed(0) ?? "—"}
                  </div>
                  <div className="text-lg font-bold uppercase"
                        style={{ color: tt.text }}
                        data-testid="portfolio-health-tier">
                    {dash.tier}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {dash.engine_version} · conf {(dash.confidence * 100).toFixed(0)}%
                </div>
              </div>
              <div className="flex-1 min-w-[400px] grid grid-cols-3 gap-2">
                {(dash.factors || []).map((f) => (
                  <div key={f.key}
                        data-testid={`portfolio-factor-${f.key}`}
                        className="flex flex-col">
                    <div className="flex justify-between text-[10px] text-slate-600">
                      <span>{f.label}</span>
                      <span className="tabular-nums font-semibold">{f.score.toFixed(0)}</span>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden mt-0.5">
                      <div className="h-full"
                            style={{ width: `${f.score}%`,
                                      background: tt.border }}/>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      w {f.weight}% · contr {f.contribution.toFixed(1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {(dash.data_gaps || []).length > 0 && (
              <div className="mt-3 text-[11px] text-amber-700 inline-flex items-center gap-1"
                    data-testid="portfolio-data-gaps">
                <AlertTriangle size={11}/>
                Data gaps: {dash.data_gaps.join(", ")}
              </div>
            )}
          </div>
        )}

        {/* KPI banner */}
        <div className="grid gap-3 md:grid-cols-6 mb-4"
              data-testid="portfolio-kpi-row">
          {[
            { label: "Portfolio Value",  value: fmt$(m.total_value),
              testid: "kpi-value",       icon: DollarSign },
            { label: "Equity",           value: fmt$(m.total_equity),
              testid: "kpi-equity",      icon: TrendingUp },
            { label: "Cash Flow / mo",   value: fmt$(m.monthly_cash_flow),
              testid: "kpi-cashflow",    icon: Activity },
            { label: "NOI (annual)",     value: fmt$(m.noi_annualized),
              testid: "kpi-noi",         icon: DollarSign },
            { label: "Cap Rate",         value: fmtPct(m.cap_rate_pct),
              testid: "kpi-caprate",     icon: Percent },
            { label: "Occupancy",        value: fmtPct(m.occupancy_pct),
              testid: "kpi-occupancy",   icon: Building2 },
          ].map((k) => (
            <div key={k.label} data-testid={k.testid}
                  className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  {k.label}
                </div>
                <k.icon size={14} className="text-slate-400"/>
              </div>
              <div className="text-lg font-bold text-slate-900 tabular-nums mt-1">
                {k.value}
              </div>
            </div>
          ))}
        </div>

        {/* Alerts strip */}
        {dash && (dash.alerts || []).length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 mb-4"
                data-testid="portfolio-alerts-strip">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={14} className="text-amber-600"/>
              <div className="text-sm font-semibold text-slate-900">
                Active Alerts ({dash.alert_count})
              </div>
            </div>
            <div className="space-y-1.5">
              {dash.alerts.slice(0, 5).map((a) => (
                <div key={a.id}
                      data-testid={`alert-row-${a.id}`}
                      className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                        style={{ color: (SEV_TONE[a.severity] || SEV_TONE.low).text,
                                  background: (SEV_TONE[a.severity] || SEV_TONE.low).bg }}>
                    {a.code}
                  </span>
                  <span className="text-slate-700">{a.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assets table */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="portfolio-search-input"
              placeholder="Search address, market, notes…"
              className="w-full pl-9 pr-3 py-2 rounded-md text-sm
                          border border-slate-200 bg-white
                          focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>
        </div>
        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 size={20} className="animate-spin text-slate-400"/>
          </div>
        ) : assets.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300
                            bg-white p-12 text-center"
                data-testid="portfolio-empty">
            <Building2 className="mx-auto text-slate-300 mb-3" size={40}/>
            <div className="text-sm text-slate-500">
              No assets yet. Click <b>Add Asset</b> to build your portfolio.
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                data-testid="portfolio-assets-table">
            <div className="grid grid-cols-[minmax(0,1.5fr)_100px_100px_100px_100px_100px_20px]
                              gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider
                              font-semibold text-slate-500 border-b border-slate-100
                              bg-slate-50/70">
              <div>Asset</div><div>Value</div><div>Equity</div>
              <div>Cash Flow</div><div>Occupancy</div><div>Strategy</div><div/>
            </div>
            {assets.map((a) => {
              const eq = (a.current_value || 0) - (a.loan_balance || 0);
              const cf = (a.monthly_rent || 0) - (a.monthly_expenses || 0)
                              - (a.monthly_debt_service || 0);
              return (
                <button key={a.id}
                          onClick={() => setSelected(a)}
                          data-testid={`asset-row-${a.id}`}
                          className="grid grid-cols-[minmax(0,1.5fr)_100px_100px_100px_100px_100px_20px]
                                      gap-3 px-4 py-3 items-center text-left w-full
                                      border-t border-slate-50 hover:bg-teal-50/40 transition">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {a.address}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {a.market || a.city} · {a.state}
                    </div>
                  </div>
                  <div className="text-sm tabular-nums">{fmt$(a.current_value)}</div>
                  <div className="text-sm tabular-nums">{fmt$(eq)}</div>
                  <div className={`text-sm tabular-nums font-semibold ${cf < 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {fmt$(cf)}
                  </div>
                  <div className="text-xs text-slate-600 capitalize">
                    {a.occupancy_status || "—"}
                  </div>
                  <div className="text-xs text-slate-600 capitalize">
                    {a.strategy || "—"}
                  </div>
                  <ChevronRight size={16} className="text-slate-300"/>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateAssetModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {selected && (
        <AssetDrawer asset={selected}
                       onClose={() => { setSelected(null); load(); }}
                       onChanged={load}/>
      )}
    </div>
  );
}


function CreateAssetModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    address: "", city: "", state: "", market: "",
    strategy: "rental", acquisition_price: "",
    current_value: "", loan_balance: "",
    interest_rate: "", monthly_rent: "",
    monthly_expenses: "", monthly_debt_service: "",
    occupancy_status: "occupied",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.address.trim()) return toast.error("Address is required");
    setSaving(true);
    try {
      const body = { ...f };
      ["acquisition_price", "current_value", "loan_balance", "interest_rate",
        "monthly_rent", "monthly_expenses", "monthly_debt_service"
      ].forEach((k) => { body[k] = f[k] ? parseFloat(f[k]) : undefined; });
      await api.post("/v3/portfolio/assets", body);
      toast.success("Asset added");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50
                      flex items-center justify-center p-4"
          onClick={onClose} data-testid="portfolio-create-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold text-slate-900">Add Portfolio Asset</div>
          <button onClick={onClose} className="text-slate-400"><X size={18}/></button>
        </div>
        <div className="grid gap-3 grid-cols-2">
          {[
            { k: "address",  label: "Address *", col: 2, testid: "asset-field-address" },
            { k: "city",     label: "City",     testid: "asset-field-city" },
            { k: "state",    label: "State",    testid: "asset-field-state" },
            { k: "market",   label: "Market",   testid: "asset-field-market" },
            { k: "occupancy_status", label: "Occupancy",
              opts: ["occupied", "vacant", "partial", "unknown"],
              testid: "asset-field-occupancy" },
            { k: "strategy", label: "Strategy",
              opts: ["rental", "brrrr", "flip", "airbnb", "creative", "commercial", "other"],
              testid: "asset-field-strategy" },
            { k: "acquisition_price", label: "Acq Price", type: "number", testid: "asset-field-acq-price" },
            { k: "current_value",     label: "Current Value", type: "number", testid: "asset-field-current-value" },
            { k: "loan_balance",      label: "Loan Balance", type: "number", testid: "asset-field-loan-balance" },
            { k: "interest_rate",     label: "Interest Rate %", type: "number", testid: "asset-field-interest-rate" },
            { k: "monthly_rent",      label: "Monthly Rent", type: "number", testid: "asset-field-monthly-rent" },
            { k: "monthly_expenses",  label: "Monthly Expenses", type: "number", testid: "asset-field-monthly-expenses" },
            { k: "monthly_debt_service", label: "Monthly Debt Service", type: "number", col: 2, testid: "asset-field-monthly-debt" },
          ].map((fld) => (
            <label key={fld.k}
                    className={`text-xs text-slate-600 ${fld.col === 2 ? "col-span-2" : ""}`}>
              {fld.label}
              {fld.opts ? (
                <select value={f[fld.k]}
                          onChange={(e) => setF({ ...f, [fld.k]: e.target.value })}
                          className="mt-1 w-full px-3 py-2 rounded-md text-sm
                                      border border-slate-200 bg-white">
                  {fld.opts.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input type={fld.type || "text"}
                         value={f[fld.k]}
                         onChange={(e) => setF({ ...f, [fld.k]: e.target.value })}
                         data-testid={fld.testid}
                         className="mt-1 w-full px-3 py-2 rounded-md text-sm
                                     border border-slate-200"/>
              )}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose}
                    className="text-sm px-3 py-2 rounded-md border border-slate-200">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
                    data-testid="asset-create-submit"
                    className="text-sm px-4 py-2 rounded-md bg-teal-500 text-white
                                hover:bg-teal-600 disabled:opacity-50 flex items-center gap-1">
            {saving && <Loader2 size={13} className="animate-spin"/>}
            Add Asset
          </button>
        </div>
      </div>
    </div>
  );
}


function AssetDrawer({ asset, onClose, onChanged }) {
  const [tab, setTab] = useState("overview");
  const TABS = [
    { id: "overview",  label: "Overview" },
    { id: "financials", label: "Financials" },
    { id: "debt",      label: "Debt" },
    { id: "advisor",   label: "AI Advisor" },
  ];
  const eq = (asset.current_value || 0) - (asset.loan_balance || 0);
  const cf = (asset.monthly_rent || 0) - (asset.monthly_expenses || 0)
                - (asset.monthly_debt_service || 0);
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end"
          onClick={onClose} data-testid="asset-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 z-10 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-teal-600">
                {asset.strategy?.replace(/_/g, " ") || "asset"}
              </div>
              <div className="text-xl font-bold text-slate-900 mt-0.5"
                    data-testid="drawer-asset-address">
                {asset.address}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {asset.city}, {asset.state} · {asset.market}
              </div>
            </div>
            <button onClick={onClose}
                      data-testid="drawer-close"
                      className="text-slate-400"><X size={20}/></button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[9px]">Value</div>
              <div className="text-lg font-bold tabular-nums">{fmt$(asset.current_value)}</div>
            </div>
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[9px]">Equity</div>
              <div className="text-lg font-bold tabular-nums">{fmt$(eq)}</div>
            </div>
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[9px]">Cash Flow</div>
              <div className={`text-lg font-bold tabular-nums ${cf < 0 ? "text-red-600" : "text-emerald-700"}`}>
                {fmt$(cf)}
              </div>
            </div>
          </div>
          <div className="flex gap-1 mt-4" data-testid="drawer-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`drawer-tab-${t.id}`}
                        className={`text-xs px-3 py-1.5 rounded-md transition
                          ${tab === t.id
                            ? "bg-teal-500 text-white"
                            : "text-slate-600 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-5 space-y-4">
          {tab === "overview" && (
            <div className="grid grid-cols-2 gap-3 text-xs"
                  data-testid="drawer-overview">
              {[
                ["Acquisition Price",  fmt$(asset.acquisition_price)],
                ["Current Value",      fmt$(asset.current_value)],
                ["Loan Balance",       fmt$(asset.loan_balance)],
                ["Interest Rate",      asset.interest_rate ? `${asset.interest_rate}%` : "—"],
                ["Monthly Rent",       fmt$(asset.monthly_rent)],
                ["Monthly Expenses",   fmt$(asset.monthly_expenses)],
                ["Monthly Debt",       fmt$(asset.monthly_debt_service)],
                ["Occupancy",          asset.occupancy_status || "—"],
                ["Cap Rate Target",    asset.cap_rate_target ? `${asset.cap_rate_target}%` : "—"],
                ["Maturity Date",      asset.maturity_date?.slice(0, 10) || "—"],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-slate-100
                                          bg-slate-50/40 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {k}
                  </div>
                  <div className="text-sm font-medium text-slate-800 mt-1">{v}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "financials" && (
            <div className="space-y-2 text-sm text-slate-600"
                  data-testid="drawer-financials">
              Monthly Net Cash Flow — <b>{fmt$(cf)}</b><br/>
              Annualized NOI (rent − expenses × 12) — <b>{fmt$((asset.monthly_rent - asset.monthly_expenses) * 12)}</b><br/>
              Est. Cap Rate — <b>{asset.current_value ? fmtPct(((asset.monthly_rent - asset.monthly_expenses) * 12) / asset.current_value * 100) : "—"}</b>
            </div>
          )}
          {tab === "debt" && (
            <div className="space-y-2 text-sm text-slate-600" data-testid="drawer-debt">
              LTV — <b>{asset.current_value ? fmtPct((asset.loan_balance / asset.current_value) * 100) : "—"}</b><br/>
              Interest — <b>{asset.interest_rate ? `${asset.interest_rate}%` : "—"}</b><br/>
              Maturity — <b>{asset.maturity_date?.slice(0, 10) || "—"}</b>
            </div>
          )}
          {tab === "advisor" && (
            <AdvisorPanel/>
          )}
        </div>
      </div>
    </div>
  );
}


const ADVISOR_MODES = [
  { id: "health_summary",              label: "Health Summary" },
  { id: "diversification",             label: "Diversification" },
  { id: "refinancing_opportunities",   label: "Refinance" },
  { id: "disposition_recommendations", label: "Disposition" },
];


function AdvisorPanel() {
  const [mode, setMode] = useState("health_summary");
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadLatest = useCallback(async (m) => {
    try {
      const { data } = await api.get("/v3/portfolio/advisor/latest",
        { params: { mode: m } });
      setAdvisor(data);
    } catch (err) {
      if (err?.response?.status === 404) setAdvisor(null);
    }
  }, []);
  useEffect(() => { loadLatest(mode); }, [mode, loadLatest]);

  const generate = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.post("/v3/portfolio/advisor",
        { mode, force });
      setAdvisor(data);
      if (data.cached) toast("Loaded cached advisor");
      else toast.success("Advisor generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Advisor failed");
    } finally { setLoading(false); }
  };
  const fb = async (rating) => {
    if (!advisor?.id) return;
    try {
      await api.post(`/v3/portfolio/advisor/${advisor.id}/feedback`, { rating });
      toast.success("Feedback recorded");
    } catch { toast.error("Feedback failed"); }
  };

  return (
    <div className="space-y-3" data-testid="drawer-advisor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1" data-testid="advisor-mode-tabs">
          {ADVISOR_MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)}
                      data-testid={`advisor-mode-${m.id}`}
                      className={`text-[11px] px-2 py-1 rounded-md ring-1 transition
                        ${mode === m.id
                          ? "bg-violet-600 text-white ring-violet-600"
                          : "bg-white text-slate-600 ring-slate-200 hover:ring-violet-300"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => generate(false)} disabled={loading}
                    data-testid="advisor-generate-btn"
                    className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                                from-violet-500 to-fuchsia-500 text-white
                                hover:from-violet-600 hover:to-fuchsia-600
                                flex items-center gap-1 disabled:opacity-50">
            {loading
              ? <Loader2 size={13} className="animate-spin"/>
              : <Sparkles size={13}/>}
            {advisor ? "Refresh" : "Generate"}
          </button>
          {advisor && (
            <button onClick={() => generate(true)} disabled={loading}
                      data-testid="advisor-force-btn"
                      className="text-xs px-2 py-1.5 rounded-md border
                                  border-slate-200 hover:bg-slate-50"
                      title="Ignore cache">
              <RotateCw size={13}/>
            </button>
          )}
        </div>
      </div>

      {!advisor && !loading && (
        <div className="text-xs text-slate-500 italic py-6 text-center"
              data-testid="advisor-empty">
          No advisor output for this mode yet — click Generate.
        </div>
      )}
      {advisor && (
        <div className="space-y-3">
          <div className="text-sm text-slate-800 font-medium leading-snug"
                data-testid="advisor-headline">
            {advisor.headline}
            {advisor.cached && (
              <span className="ml-2 text-[10px] uppercase tracking-wider
                                px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">cached</span>
            )}
            <span className="ml-2 text-[10px] text-slate-500">
              conf {(advisor.confidence * 100).toFixed(0)}%
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-3 text-xs">
            {[
              { key: "strengths",           title: "Strengths",   tone: "emerald" },
              { key: "opportunities",       title: "Opportunities", tone: "amber" },
              { key: "recommended_actions", title: "Actions",     tone: "violet" },
            ].map((sec) => (
              <div key={sec.key}
                    data-testid={`advisor-section-${sec.key}`}
                    className={`rounded-lg border p-2.5 bg-${sec.tone}-50/40
                                border-${sec.tone}-200`}>
                <div className={`text-[10px] uppercase tracking-wider
                                  text-${sec.tone}-700 font-semibold mb-1`}>
                  {sec.title}
                </div>
                <ul className="space-y-1.5">
                  {(advisor[sec.key] || []).length === 0 && (
                    <li className="text-slate-400 italic">—</li>
                  )}
                  {(advisor[sec.key] || []).map((it, i) => (
                    <li key={i} className="text-slate-700 leading-snug">
                      {typeof it === "string" ? it : it.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[10px] text-slate-400">
              {advisor.model_name} · {advisor.prompt_version}
            </div>
            <div className="flex gap-1">
              <button onClick={() => fb("helpful")}
                        data-testid="advisor-fb-helpful"
                        className="text-[11px] px-2 py-1 rounded border border-slate-200
                                    hover:bg-emerald-50 flex items-center gap-1 text-slate-600">
                <ThumbsUp size={11}/> Helpful
              </button>
              <button onClick={() => fb("not_helpful")}
                        data-testid="advisor-fb-not-helpful"
                        className="text-[11px] px-2 py-1 rounded border border-slate-200
                                    hover:bg-red-50 flex items-center gap-1 text-slate-600">
                <ThumbsDown size={11}/> Not Helpful
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
